import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "CRM Strategy and Implementation",
  seoDescription: "CRM strategy that unifies customer data, automates processes, and provides visibility into every relationship. Build a foundation for scalable growth.",
  canonicalUrl: "https://xperfomax.com/crm-strategy",
  category: "services",
  categoryLabel: "Services / CRM Strategy",
  headline: "CRM Strategy and Implementation",
  subheadline: "CRM strategy that unifies customer data, automates processes, and provides visibility into every relationship. Build a foundation for scalable growth.",
  ctaText: "Start a growth conversation",
  problemTitle: "Why CRM Projects Underdeliver",
  problems: [
    "CRM becomes a data dump, not a strategic tool.",
    "Data quality degrades without governance processes.",
    "Teams work around the CRM instead of through it.",
    "No clear ROI visibility from CRM investment.",
  ],
  systemTitle: "Our CRM System",
  systemDescription: "Data architecture, process automation, and governance designed for adoption and measurable business impact.",
  services: [
    { name: "CRM Architecture", icon: "Layers", components: ["Data model design", "Object and relationship mapping", "Scalability planning"], outcome: "A CRM foundation built for growth, not retrofitted later." },
    { name: "Data Integration", icon: "Database", components: ["Source system mapping", "Real-time synchronization", "Data quality management"], outcome: "Unified customer data across all touchpoints." },
    { name: "Process Automation", icon: "Workflow", components: ["Workflow design and build", "Lead routing logic", "Task automation"], outcome: "Automated processes that scale without manual overhead." },
    { name: "Segmentation Framework", icon: "GitBranch", components: ["Behavioral segmentation", "Lifecycle stage assignment", "Dynamic list management"], outcome: "Segments that power personalization across all channels." },
    { name: "Reporting & Dashboards", icon: "BarChart3", components: ["Custom report development", "Executive dashboards", "Pipeline and revenue analytics"], outcome: "Clear visibility into customer relationships and revenue." },
    { name: "Governance & Compliance", icon: "Shield", components: ["Data governance policies", "Privacy compliance setup", "User access management"], outcome: "Clean, compliant data that teams can trust." },
  ],
  processTitle: "From assessment to optimization.",
  processSteps: [
    { number: "01", title: "CRM Assessment", description: "Audit current CRM state, data quality, and process gaps to establish baseline and prioritize improvements." },
    { number: "02", title: "Architecture Design", description: "Design data model, object relationships, and integration architecture aligned with business processes." },
    { number: "03", title: "Data Migration & Integration", description: "Clean, transform, and migrate data while building integrations with source systems." },
    { number: "04", title: "Process & Automation Build", description: "Implement workflows, lead routing, segmentation rules, and task automation." },
    { number: "05", title: "Training & Optimization", description: "Enable teams with training and continuously optimize based on adoption metrics and feedback." },
  ],
  timelinePhases: [
    { number: "01", title: "CRM Assessment", description: "Audit current state and identify gaps.", decision: "What to fix first based on impact.", output: "Assessment report with prioritized roadmap.", riskRemoved: "Building on a broken foundation." },
    { number: "02", title: "Architecture Design", description: "Design data model and integration architecture.", decision: "How to structure data for flexibility and scale.", output: "Technical architecture documentation.", riskRemoved: "Data models that limit future growth." },
    { number: "03", title: "Data Migration & Integration", description: "Clean and migrate data, build integrations.", decision: "What data to migrate and transform.", output: "Unified CRM with connected data sources.", riskRemoved: "Data silos and quality issues." },
    { number: "04", title: "Process & Automation Build", description: "Implement workflows and automation.", decision: "Which processes to automate first.", output: "Live automation with documented workflows.", riskRemoved: "Manual processes that do not scale." },
    { number: "05", title: "Training & Optimization", description: "Enable teams and continuously improve.", decision: "How to drive adoption and iterate.", output: "Trained teams with optimization playbook.", riskRemoved: "Low adoption undermining CRM investment." },
  ],
  technologyCategories: [
    { title: "CRM Platforms", icon: "Users", items: ["Salesforce", "HubSpot", "Pipedrive", "Zoho CRM"] },
    { title: "Integration", icon: "Settings", items: ["Zapier", "Make", "Segment", "Tray.io"] },
    { title: "Analytics", icon: "LineChart", items: ["Looker Studio", "Tableau", "Power BI"] },
    { title: "Data Quality", icon: "Target", items: ["ZoomInfo", "Clearbit", "NeverBounce"] },
  ],
  techTitle: "CRM and Data Tools",
  techDescription: "Enterprise-grade CRM platforms and integration technologies.",
  qualification: {
    forYou: [
      "You have a growing customer base that needs systematic management",
      "You are ready to invest in proper CRM infrastructure",
      "You want data-driven decision making across sales and marketing",
      "You need better visibility into customer relationships",
    ],
    notForYou: [
      "You have fewer than 100 customers and simple needs",
      "You are not ready to commit to CRM adoption and training",
      "You want a quick fix without process change",
      "You do not have resources to maintain the CRM long-term",
    ],
  },
  faqs: [
    { question: "How long does a CRM implementation take?", answer: "A foundational CRM setup takes 8 to 12 weeks. Full implementations with complex integrations, migrations, and automation typically take 3 to 6 months depending on scope." },
    { question: "Should we migrate to a new CRM or optimize our current one?", answer: "It depends on your current platform limitations and business needs. We assess both options and recommend the path that delivers the best ROI with the least disruption." },
    { question: "How do you handle data quality issues?", answer: "We include data cleansing as part of every CRM project. This includes deduplication, standardization, enrichment, and ongoing governance processes to maintain quality." },
    { question: "What CRM platforms do you work with?", answer: "We have deep expertise in Salesforce and HubSpot, and experience with Pipedrive, Zoho, and other platforms. We are platform-agnostic and recommend based on your needs." },
  ],
  finalCtaTitle: "Build a CRM that drives growth.",
  finalCtaDescription: "Most CRMs are expensive databases. We build strategic systems that power customer relationships and revenue.",
  finalCtaButtonText: "Start a growth conversation",
};

const CRMStrategy = () => {
  const { content } = usePageContent("crm-strategy", fallback);

  const services = content.services.map((s: any) => ({ ...s, icon: resolveIcon(s.icon) }));
  const technologyCategories = content.technologyCategories.map((t: any) => ({ ...t, icon: resolveIcon(t.icon) }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      timelinePhases={content.timelinePhases}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default CRMStrategy;
